import { useState } from "react";
import { getFontAwesomeIcon } from "../../../utils";
import TertiaryButton from "../BaseButton/TertiaryButton";

function DocumentUploader() {
	const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
	const [isDragging, setIsDragging] = useState(false);
	const [uploading, setUploading] = useState(false);

	const handleDragEnter = (e: React.DragEvent<HTMLDivElement>) => {
		e.preventDefault();
		e.stopPropagation();
		setIsDragging(true);
	};

	const handleDragLeave = (e: React.DragEvent<HTMLDivElement>) => {
		e.preventDefault();
		e.stopPropagation();
		setIsDragging(false);
	};

	const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
		e.preventDefault();
		e.stopPropagation();
	};

	const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
		e.preventDefault();
		e.stopPropagation();
		setIsDragging(false);

		const files = Array.from(e.dataTransfer.files);
		handleFiles(files);
	};

	const handleFileInput = (e: React.ChangeEvent<HTMLInputElement>) => {
		const files = Array.from(e.target.files || []);
		handleFiles(files);
	};

	const handleFiles = (files: File[]) => {
		const allowedTypes = [
			"application/pdf",
			"application/msword",
			"application/vnd.openxmlformats-officedocument.wordprocessingml.document",
			"text/plain",
		];

		const validFiles = files.filter(
			(file) =>
				allowedTypes.includes(file.type) ||
				file.name.match(/\.(pdf|docx?|txt)$/i)
		);

		setSelectedFiles((prev) => [...prev, ...validFiles]);
	};

	const removeFile = (index: number) => {
		setSelectedFiles((prev) => prev.filter((_, i) => i !== index));
	};

	const handleUpload = async () => {
		if (selectedFiles.length === 0) return;

		setUploading(true);
		const formData = new FormData();

		selectedFiles.forEach((file) => {
			formData.append("files", file);
		});

		try {
			const response = await fetch("/api/upload", {
				method: "POST",
				body: formData,
			});

			if (response.ok) {
				alert("Files uploaded successfully!");
				setSelectedFiles([]);
			} else {
				alert("Upload failed");
			}
		} catch (error) {
			console.error("Error uploading files:", error);
			alert("Error uploading files");
		} finally {
			setUploading(false);
		}
	};

	return (
		<div className="w-full ">
			<div
				onDragEnter={handleDragEnter}
				onDragOver={handleDragOver}
				onDragLeave={handleDragLeave}
				onDrop={handleDrop}
				className={` w-full px-2.5 py-6 rounded-lg  outline-dashed outline-2 outline-offset-[-2px] inline-flex flex-col justify-center items-center gap-1 cursor-pointer transition-colors ${
					isDragging
						? "bg-blue-50 outline-indigo-700"
						: "bg-white outline-gray-200"
				}`}
			>
				<input
					type="file"
					multiple
					onChange={handleFileInput}
					accept=".pdf,.doc,.docx,.txt"
					className="hidden"
					id="fileInput"
				/>
				<label
					htmlFor="fileInput"
					className="cursor-pointer flex flex-col items-center gap-y-2"
				>
					<div
						data-padding="false"
						data-scale=".75x"
						data-style="regular"
						className="w-3 h-3 relative"
					>
						{getFontAwesomeIcon("cloudArrowUp", "regular")}
					</div>
					<div className="inline-flex justify-center items-center gap-1 font-omnes-medium">
						<div className="justify-start text-gray-800 text-sm font-medium tracking-tight">
							Drop your file(s) or
						</div>
						<div
							data-disabled="no"
							data-haslefticon="false"
							data-hasrighticon="false"
							data-hover="no"
							className="h-6 flex justify-start items-center"
						>
							<div className="justify-start text-supply-blue-400 hover:underline hover:text-supply-blue-300 text-sm font-medium line-clamp-1"></div>
							<TertiaryButton text={"click to browse"} />
						</div>
					</div>
					<div className="inline-flex justify-center items-center">
						<div className="justify-start text-gray-600 text-sm font-normal tracking-tight">
							Supported files: .pdf, .docx, .txt
						</div>
					</div>
				</label>
			</div>

			{selectedFiles.length > 0 && (
				<div className="mt-6">
					<h3 className="text-base font-semibold mb-3 text-gray-900">
						Selected Files
					</h3>

					{selectedFiles.map((file, index) => (
						<div
							key={index}
							className="flex justify-between items-center p-3 border border-gray-200 rounded-lg mb-2 bg-white"
						>
							<div className="flex-1">
								<p className="text-sm font-medium text-gray-900 mb-1">
									{file.name}
								</p>
								<p className="text-xs text-gray-600">
									{(file.size / 1024).toFixed(2)} KB
								</p>
							</div>
							<button
								onClick={() => removeFile(index)}
								className="text-red-500 text-sm font-medium px-2 py-1 hover:text-red-700"
							>
								Remove
							</button>
						</div>
					))}

					<button
						onClick={handleUpload}
						disabled={uploading}
						className={`mt-4 w-full py-3 rounded-lg text-sm font-medium text-white transition-colors ${
							uploading
								? "bg-gray-400 cursor-not-allowed"
								: "bg-indigo-700 hover:bg-indigo-800 cursor-pointer"
						}`}
					>
						{uploading
							? "Uploading..."
							: `Upload ${selectedFiles.length} file(s)`}
					</button>
				</div>
			)}
		</div>
	);
}

export default DocumentUploader;
